import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import {
	userManagementColumns,
} from "../../../utils/constants";
import AdminTables from "../../common/adminTables";
import { Alert as AlertType } from "../../../utils/interfaces";
import { ConfirmationModal } from "../../common/modal";
import adminConfirmation from "../../common/modal/modalBody/adminConfirmation";
import AdminLayout from "../../common/adminLayout";
import AddUserIcon from "../../../assets/svg/addUserIcon";
import Register from "../../public/register";
import { getUserManagementColumns } from "../../../utils/constants";
import Alert from "../../common/alerts/alert";

// Custom title component with search field - defined outside to prevent re-creation
const TitleWithSearch: React.FC<{
	searchValue: string;
	onSearchChange: (value: string) => void;
}> = ({ searchValue, onSearchChange }) => (
	<div className="d-flex align-items-center gap-3">
		<h2 className="m-0" style={{
			fontSize: '1.25rem',
			fontWeight: 'bold',
			fontFamily: 'Roboto',
		}}>User Management</h2>
		<div className="position-relative" style={{ minWidth: '300px' }}>
			<input
				type="text"
				className="form-control bg-white"
				placeholder="Search here"
				value={searchValue}
				onChange={(e) => onSearchChange(e.target.value)}
				style={{
					borderRadius: '20px',
					paddingLeft: '20px',
					fontSize: '14px'
				}}
			/>
			<i 
				className="bi bi-search position-absolute"
				style={{
					right: '12px',
					top: '50%',
					transform: 'translateY(-50%)',
					color: '#26397E',
					stroke: '#26397E',
					fontWeight: 'bolder',
				}}
			></i>
		</div>
	</div>
);

const UserManagement = () => {
	const navigate = useNavigate();
	const { userData, logout } = useAuth();
	const [formData, setFormData] = useState<{
		results: [];
		total_count: number;
	}>({ results: [], total_count: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(3);
	const [isLoading, setIsLoading] = useState(true);
	const [alert, setAlert] = useState<AlertType | null>(null);
	const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
	const [selectedRowData, setSelectedRowData] = useState<any>(null);
	const [showModal, setShowModal] = useState(false);
	const [selectedAction, setSelectedAction] = useState<string | null>(null);
	const [selectedAccountType, setSelectedAccountType] = useState<number | null>(
		null
	);
	const [showRegisterSideScreen, setShowRegisterSideScreen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editUserData, setEditUserData] = useState<any>(null);
	const [triggerAccountTypeChange, setTriggerAccountTypeChange] = useState(false);
	const [searchValue, setSearchValue] = useState("");

	const handleAlert = (message: string, type: "success" | "danger") => {
		setAlert({
			message,
			type,
			show: true
		});
	};

	// Custom debounce hook
	function useDebounce<T extends (...args: any[]) => any>(
		callback: T,
		delay: number
	  ) {
		const timeoutRef = useRef<NodeJS.Timeout | null>(null);
		
		return useCallback((...args: Parameters<T>) => {
		  if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		  }
		  
		  timeoutRef.current = setTimeout(() => {
			callback(...args);
		  }, delay);
		}, [callback, delay]);
	}

	const debouncedFetchData = useDebounce((columnId: string, value: string) => {
		if (value.trim() === '') {
			// If search value is empty, reset to normal data
			getFormData();
			return;
		}
	
		// Proceed with search
		fetchFilteredUsers(columnId, value);
	}, 500);
	
	// Helper function to fetch filtered users with fallback search
	const fetchFilteredUsers = async (columnId: string, value: string) => {
		try {
			// Create params object with pagination
			const params: Record<string, any> = {
				offset: (currentPage - 1) * itemsPerPage,
				limit: itemsPerPage,
			};
			
			// Add search parameters if column and value exist
			if (columnId && value) {
				buildSearchParams(params, columnId, value);
			}
			
			const response = await createAxiosInstance().get("/users", { params });
			
			// If global search returns no results, try fallback searches
			if (columnId === 'global_search' && response.data.results.length === 0 && value.trim()) {
				await tryFallbackSearch(value.trim());
				return;
			}
			
			setFormData(response.data);
		} catch (error: any) {
			handleSearchError(error);
		}
	};
	
	// Helper function to try fallback searches for global search
	const tryFallbackSearch = async (value: string) => {
		const fallbackFields = ['email', 'company', 'title', 'industry', 'last_name'];
		
		for (const field of fallbackFields) {
			try {
				const params: Record<string, any> = {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
					[field]: value
				};
				
				const response = await createAxiosInstance().get("/users", { params });
				if (response.data.results.length > 0) {
					setFormData(response.data);
					return;
				}
			} catch (error) {
				// Continue to next field if this one fails
				continue;
			}
		}
		
		// If no fallback found results, set empty results
		setFormData({ results: [], total_count: 0 });
	};
	
	// Helper function to build search parameters
	const buildSearchParams = (params: Record<string, any>, columnId: string, value: string) => {
		if (columnId === 'global_search') {
			// Global search across multiple columns
			handleGlobalSearch(params, value);
		} else if (columnId === 'full_name') {
			handleFullNameSearch(params, value);
		} else if (columnId === 'account_type') {
			handleAccountTypeSearch(params, columnId, value);
		} else {
			params[columnId] = value;
		}
	};
	
	// Helper function for global search across multiple columns
	const handleGlobalSearch = (params: Record<string, any>, value: string) => {
		const searchTerm = value.trim();
		const searchTermLower = searchTerm.toLowerCase();
		
		// Email pattern detection
		if (searchTerm.includes('@')) {
			params['email'] = value;
			return;
		}
		
		// Account type pattern detection
		const accountTypeMap: Record<string, number> = {
			'free': 0,
			'premium': 1,
			'advisor': 2,
			'service provider': 3
		};
		
		if (searchTermLower in accountTypeMap) {
			params['account_type'] = accountTypeMap[searchTermLower];
			return;
		}
		
		// Numeric pattern (likely ID)
		if (/^\d+$/.test(searchTerm)) {
			params['id'] = value;
			return;
		}
		
		// Name pattern (contains space - likely full name)
		const nameParts = searchTerm.split(' ');
		if (nameParts.length > 1) {
			params['first_name'] = nameParts[0];
			params['last_name'] = nameParts.slice(1).join(' ');
			return;
		}
		
		// Single word search - prioritize first name as most common search
		// This provides the best user experience for name-based searches
		params['first_name'] = value;
	};
	
	// Helper function for full_name search
	const handleFullNameSearch = (params: Record<string, any>, value: string) => {
		const nameParts = value.trim().split(' ');
		if (nameParts.length > 1) {
			params['first_name'] = nameParts[0];
			params['last_name'] = nameParts.slice(1).join(' ');
		} else {
			params['first_name'] = value;
			// params['last_name'] = value;
		}
	};
	
	// Helper function for account_type search
	const handleAccountTypeSearch = (params: Record<string, any>, columnId: string, value: string) => {
		const accountTypeMap: Record<string, number> = {
			'free': 0,
			'premium': 1,
			'advisor': 2,
			'service provider': 3,
			'account manager': 4,
			'fma manager': 5
		};
		
		const normalizedValue = value.trim().toLowerCase();
		if (normalizedValue in accountTypeMap) {
			params[columnId] = accountTypeMap[normalizedValue];
		} else {
			params[columnId] = value;
		}
	};
	
	// Helper function to handle search errors
	const handleSearchError = (error: any) => {
		handleApiError(error, "Error searching users");
		setFormData({ results: [], total_count: 0 });
		handleAlert("Error searching users", "danger");
	};

	const fetchDataFromServer = async (columnId: string, value: string) => {
		console.log(columnId, value, "columnId, value");
		debouncedFetchData(columnId, value);
	};

	const handleApiError = (error: any, message: string = "An error occurred") => {
		if (error.status == 401 || error.status == 403) {
			logout();
			navigate("/");
		}
		console.error(message, error);
	};
	
	const getUsers = async () => {
		try {
			const response = await createAxiosInstance().get("/users", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				},
			});

			return response;
		} catch (error: any) {
			handleApiError(error, "Error fetching users");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getFormData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });
			const response = await getUsers();
			setFormData(response.data);
		} catch (error: any) {
			handleApiError(error, "Error loading form data");
		} finally {
			setIsLoading(false);
		}
	};

	const refreshData = () => {
		// If there's an active search, refresh with search params
		if (searchValue && searchValue.trim() !== '') {
			fetchFilteredUsers('global_search', searchValue);
		} else {
			// Otherwise, fetch all users
			getFormData();
		}
	};

	useEffect(() => {
		refreshData();
	}, [currentPage, itemsPerPage]);

	useEffect(() => {
		if (userData && (!userData?.is_admin && userData?.account_type !== 4)) {
			navigate("/");
		}
	}, [userData]);

	const handleRowSelection = (rowData: any) => {
		if (rowData?.id !== selectedRowId) {
			setSelectedRowId(rowData?.id);
			setSelectedRowData(rowData);
			console.log(rowData, "Row data")
		} else {
			setSelectedRowId(null);
			setSelectedRowData(null);
		}
	};

	const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const action = e.target.value;
		if (action) {
			setSelectedAction(action);
			setShowModal(true);
		}
	};

	// Unified action configuration
	const actionConfig: Record<string, { endpoint: string, resultMessage: string }> = {
		"changeAccountType": { endpoint: "/update-account-access", resultMessage: "updated" },
		"delete": { endpoint: "/delete-user", resultMessage: "deleted" },
		"suspend": { endpoint: "/suspend-user", resultMessage: "suspended" },
		"reactivate": { endpoint: "/enable-user", resultMessage: "reactivated" }
	};

	// Helper function to determine the API endpoint
	const getActionEndpoint = (action: string): string => {
		return actionConfig[action]?.endpoint || `/users/${action}`;
	};
	
	// Helper function to get success message
	const getSuccessMessage = (action: string): string => {
		const resultMessage = actionConfig[action]?.resultMessage || action;
		return `Account with id ${selectedRowId} successfully ${resultMessage}`;
	};

	// Helper function to build the payload
	const buildActionPayload = (action: string): Record<string, any> => {
		if (action === "changeAccountType") {
			return {
				new_account_type: selectedAccountType,
				user_id: selectedRowId,
			};
		}
		
		return { user_id: selectedRowId };
	};

	const handleConfirm = async () => {
		if (!selectedAction) return;
		
		try {
			const url = getActionEndpoint(selectedAction);
			const payload = buildActionPayload(selectedAction);
			
			await createAxiosInstance().put(url, payload);
			handleAlert(getSuccessMessage(selectedAction), "success");
			refreshData();
			setSelectedRowId(null);
		} catch (error: any) {
			handleApiError(error, "Error performing action");
		} finally {
			setShowModal(false);
			setSelectedAction(null);
		}
	};

	const handleCancel = () => {
		setShowModal(false);
		setSelectedAction(null);
	};

	const handleAddNewUser = () => {
		setIsEditMode(false);
		setEditUserData(null);
		setShowRegisterSideScreen(true);
	};

	const handleCloseRegisterSideScreen = () => {
		setShowRegisterSideScreen(false);
		setIsEditMode(false);
		setEditUserData(null);
	};

	const handleRegistrationSuccess = () => {
		// Refresh the table data
		refreshData();
		// Close the side screen
		setShowRegisterSideScreen(false);
		setIsEditMode(false);
		setEditUserData(null);
		// Show a success message
		const message = isEditMode ? "User updated successfully!" : "User registered successfully!";
		handleAlert(message, "success");
	};

	// Define action handlers for the columns
	const handleUserSettings = (user: any) => {
		console.log("Opening settings for user:", user.id);
		setIsEditMode(true);
		setEditUserData(user);
		setShowRegisterSideScreen(true);
	};

	const handleToggleUserBan = (user: any) => {
		// Check if user is trying to modify their own account
		if (userData?.id === user.id) {
			handleAlert("Cannot modify your own account", "danger");
			return;
		}

		// Check if current user is account manager trying to modify another account manager
		if (userData?.account_type === 4 && user.account_type === 4) {
			handleAlert("Cannot modify other account manager accounts", "danger");
			return;
		}

		console.log(userData, "userData");
		console.log(user, "user");

		console.log(user.is_enabled ? "Banning user:" : "Unbanning user:", user.id);
		setSelectedAction(user.is_enabled ? "suspend" : "reactivate");
		setSelectedRowId(user.id);
		setSelectedRowData(user);
		setShowModal(true);
	};

	const handleDeleteUser = (user: any) => {
		// Check if user is trying to delete their own account
		if (userData?.id === user.id) {
			handleAlert("Cannot delete your own account", "danger");
			return;
		}

		// Check if current user is account manager trying to delete another account manager
		if (userData?.account_type === 4 && user.account_type === 4) {
			handleAlert("Cannot delete other account manager accounts", "danger");
			return;
		}

		setSelectedAction("delete");
		setSelectedRowId(user.id);
		setSelectedRowData(user);
		setShowModal(true);
	};

	// useEffect to trigger handleConfirm when account type change is ready
	useEffect(() => {
		if (triggerAccountTypeChange && selectedAction === "changeAccountType" && selectedRowId && selectedAccountType !== null) {
			handleConfirm();
			setTriggerAccountTypeChange(false);
		}
	}, [triggerAccountTypeChange, selectedAction, selectedRowId, selectedAccountType]);

	const columns = getUserManagementColumns({
		onSettings: handleUserSettings,
		onToggleBan: handleToggleUserBan,
		onDelete: handleDeleteUser,
		onAccountTypeChange: async (user: any, newAccountType: number)=>{
			// Check if current user is account manager (account_type = 4) and trying to assign account manager role
			if (userData?.account_type === 4 && newAccountType === 4) {
				handleAlert("Cannot update to account manager", "danger");
				return;
			}
			// user trying to update their own account role
			if (userData?.id === user.id){
				handleAlert("This account role cannot be changed", "danger");
				return;
			}

			// Check if current user is account manager trying to change another account manager's role
			if (userData?.account_type === 4 && user.account_type === 4) {
				handleAlert("Cannot modify other account manager roles", "danger");
				return;
			}
			
			setSelectedAction("changeAccountType");
			setSelectedRowId(user.id);
			setSelectedRowData(user);
			setSelectedAccountType(newAccountType);
			
			// Trigger a flag to call handleModalConfirm after state updates
			setTriggerAccountTypeChange(true);
		},
	});

	// Action panel component
	const ActionPanel = () => (
		<>
			{selectedRowId && (
				<>
					<label htmlFor="floatingSelect">User Actions:</label>
					<select
						onChange={handleSelectChange}
						className="form-select mb-4"
						id="floatingSelect"
						aria-label="Floating label select example"
					>
						<option value="">Select an action</option>
						<option value="delete">Delete</option>
						{selectedRowData?.is_enabled && <option value="suspend">Suspend</option>}
						{!selectedRowData?.is_enabled && <option value="reactivate">Reactivate</option>}
						
						<option value="changeAccountType">
							Change Account Type
						</option>
					</select>
				</>
			)}
		</>
	);

	return (
		<AdminLayout
			title={<TitleWithSearch searchValue={searchValue} onSearchChange={(value) => {
				setSearchValue(value);
				fetchDataFromServer('global_search', value);
			}} />}
			// actionPanel={<ActionPanel />}
			formData={formData}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			itemsPerPage={itemsPerPage}
			setItemsPerPage={setItemsPerPage}
			alert={alert || undefined}
			setAlert={setAlert}
		>
			<div className="d-flex justify-content-end">
				<button 
					className="btn btn-rounded-blue d-flex align-items-center gap-2" 
					onClick={handleAddNewUser}
					style={{
						width: '13.8%',
						borderRadius: '77.125rem',
						transition: 'all 0.3s ease',
					}}
					onMouseEnter={(e) => {
						const target = e.target as HTMLButtonElement;
						target.style.backgroundColor = '#5376F0 !important';
						target.style.transform = 'translateY(-2px)';
					}}
					onMouseLeave={(e) => {
						const target = e.target as HTMLButtonElement;
						target.style.backgroundColor = '#26397E';
						target.style.transform = 'translateY(0)';
					}}
				>
					<AddUserIcon />
					ADD NEW USER
				</button>
			</div>
			<AdminTables
				containerStyle={{
					width: "100%",
					overflowX: "auto",
					marginTop: '0.8rem'
				}} 
				data={formData.results}
				columns={columns}
				rowSelectionEnabled={false}
				onRowSelect={handleRowSelection}
				showSearch={false}
				serverSideSearch={true}
				onServerSearch={(columnId, value) => {
					fetchDataFromServer(columnId, value);
				}}
				isLoading={isLoading}
			/>
			<ConfirmationModal
				show={showModal}
				onClose={handleCancel}
				onConfirm={handleConfirm}
				title={selectedAction === "delete" ? "Delete Confirmation" : "Suspend Account Confirmation"}
				body={adminConfirmation({ 
					action: selectedAction ?? "",
					selectedAccountType,
					setSelectedAccountType })}
				maxHeight={selectedAction === "changeAccountType" ? "30vh" : "25vh"}
				maxWidth={'43.22vw'}
			/>

			{/* Register User Side Screen */}
			{showRegisterSideScreen && (
				<>
					<div
						className="offcanvas-backdrop fade show"
						onClick={handleCloseRegisterSideScreen}
					></div>
					<div
						className="offcanvas offcanvas-end show"
						tabIndex={-1}
						style={{ 
							zIndex: 1050,
						}}
					>
						<div className="offcanvas-header">
							<h5 className="offcanvas-title" style={{
								fontSize: '1.25rem',
								fontWeight: 'bold',
								marginLeft: '1rem',
								padding: '10px 0px'
							}}>
								{isEditMode ? "Update User" : "Add New User"}
							</h5>
							<button
								type="button"
								className="btn-close"
								aria-label="Close"
								onClick={handleCloseRegisterSideScreen}
							></button>
						</div>
						<div className="offcanvas-body px-4">
							<Register 
								onSuccess={handleRegistrationSuccess}
								isUpdateMode={isEditMode}
								userData={editUserData}
								updateEndpoint="/update-user-account"
							/>
						</div>
					</div>
				</>
			)}

			{/* Alert Component */}
			{alert && (
				<Alert
					message={alert.message}
					type={alert.type}
					show={alert.show}
					onClose={() => setAlert(null)}
				/>
			)}
		</AdminLayout>
	);
};

export default UserManagement;
